import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const COLOR = { green:'var(--green)', yellow:'var(--amber)', red:'var(--red)' };

// ─── Report Issue / standalone modal ─────────────────────────────────────────
function IssueModal({ title, onClose, onSubmit }) {
  const [form, setForm] = useState({ description:'', vendor_involved:false, vendor_name:'' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!form.description.trim()) { setErr('Describe the problem'); return; }
    setBusy(true); setErr('');
    try { await onSubmit(form); onClose(); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:480 }}>
        <div className="modal-header"><span className="modal-title">{title}</span><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="form-group">
          <label>What is the problem? *</label>
          <textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} placeholder="Describe the issue…" />
        </div>
        <div className="form-group">
          <label>Vendor involved?</label>
          <div className="flex-gap" style={{ gap:8 }}>
            <button type="button" className={`btn btn-sm ${form.vendor_involved?'btn-primary':'btn-secondary'}`} onClick={() => setForm(f => ({ ...f, vendor_involved:true }))}>Yes</button>
            <button type="button" className={`btn btn-sm ${!form.vendor_involved?'btn-primary':'btn-secondary'}`} onClick={() => setForm(f => ({ ...f, vendor_involved:false, vendor_name:'' }))}>No</button>
          </div>
        </div>
        {form.vendor_involved && (
          <div className="form-group"><label>Vendor name</label><input value={form.vendor_name} onChange={e => setForm(f => ({ ...f, vendor_name:e.target.value }))} /></div>
        )}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy?'Submitting…':'Report Issue'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Today's checklist ───────────────────────────────────────────────────────
function TodayCheck({ campusId }) {
  const [data, setData] = useState(null);
  const [issueFor, setIssueFor] = useState(null);   // checklist item being reported
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!campusId) return;
    api.get(`/health-checks/today/${campusId}`).then(r => setData(r.data)).catch(()=>setData(null));
  }, [campusId]);
  useEffect(() => { load(); }, [load]);

  if (!campusId) return <div style={{ color:'var(--text2)' }}>Select a campus.</div>;
  if (!data) return <div style={{ color:'var(--text2)' }}>Loading…</div>;

  const { checklist, items, submitted_by_name } = data;
  const devices = [...new Set(items.map(i => i.device_name))];
  const pendingCount = items.filter(i => i.status === 'pending').length;
  const isComplete = checklist.status === 'complete';

  const markOk = async id => { await api.post('/health-checks/item/ok', { checklist_item_id:id }); load(); };
  const allOk = async dev => { await api.post('/health-checks/device/all-ok', { checklist_id:checklist.id, device_name:dev }); load(); };
  const reportItem = async form => { await api.post('/health-checks/item/issue', { checklist_item_id:issueFor.id, ...form }); load(); };
  const submit = async () => { setBusy(true); try { await api.post('/health-checks/submit', { checklist_id:checklist.id }); load(); } finally { setBusy(false); } };

  const pill = s => s === 'ok' ? <span style={{ color:'var(--green)', fontSize:12 }}>● OK</span>
    : s === 'issue' ? <span style={{ color:'var(--red)', fontSize:12 }}>● Issue raised</span>
    : <span style={{ color:'var(--text3)', fontSize:12 }}>○ Pending</span>;

  return (
    <div>
      {/* Status banner */}
      <div className="card" style={{ marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10,
        borderLeft:`3px solid ${isComplete?'var(--green)':checklist.status==='missed'?'var(--red)':'var(--amber)'}` }}>
        <div>
          <div style={{ fontSize:13, color:'var(--text2)' }}>{new Date().toLocaleDateString(undefined,{ weekday:'long', day:'numeric', month:'long' })}</div>
          <div style={{ fontWeight:600 }}>
            {isComplete ? `✅ Submitted${checklist.submitted_at?' at '+new Date(checklist.submitted_at).toLocaleTimeString():''}${submitted_by_name?' by '+submitted_by_name:''}`
              : checklist.status==='missed' ? '⛔ Missed' : `Pending — ${pendingCount} item(s) left`}
          </div>
        </div>
        {!isComplete && (
          <button className="btn btn-primary" onClick={submit} disabled={pendingCount>0 || busy}>
            {pendingCount>0 ? `Address ${pendingCount} item(s) to submit` : (busy?'Submitting…':"Submit Today's Checklist")}
          </button>
        )}
      </div>

      {/* Device cards */}
      {devices.map(dev => {
        const rows = items.filter(i => i.device_name === dev);
        return (
          <div key={dev} className="card" style={{ marginBottom:14, padding:0, overflow:'hidden' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:'1px solid var(--border)', background:'var(--bg2)' }}>
              <div style={{ fontWeight:600, fontSize:14 }}>🖥 {dev}</div>
              {!isComplete && rows.some(r => r.status==='pending') &&
                <button className="btn btn-secondary btn-sm" onClick={() => allOk(dev)}>Mark All OK</button>}
            </div>
            <table><tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 14px', fontSize:13 }}>{r.parameter}</td>
                  <td style={{ padding:'8px 14px', width:120 }}>{pill(r.status)}</td>
                  <td style={{ padding:'8px 14px', textAlign:'right', width:200 }}>
                    {!isComplete && r.status !== 'issue' && (
                      <div className="flex-gap" style={{ gap:6, justifyContent:'flex-end' }}>
                        {r.status !== 'ok' && <button className="btn btn-secondary btn-sm" onClick={() => markOk(r.id)}>OK</button>}
                        <button className="btn btn-danger btn-sm" onClick={() => setIssueFor(r)}>Report Issue</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody></table>
          </div>
        );
      })}

      {issueFor && <IssueModal title={`Report — ${issueFor.device_name}: ${issueFor.parameter}`} onClose={() => setIssueFor(null)} onSubmit={reportItem} />}
    </div>
  );
}

// ─── Status board (18-campus grid) ───────────────────────────────────────────
function StatusBoard({ onPick }) {
  const [grid, setGrid] = useState([]);
  useEffect(() => { api.get('/health-checks/dashboard').then(r => setGrid(r.data)).catch(()=>{}); }, []);
  return (
    <div>
      <div style={{ display:'flex', gap:16, marginBottom:14, fontSize:12, color:'var(--text2)' }}>
        <span>● <span style={{ color:'var(--green)' }}>Green</span> all OK</span>
        <span>● <span style={{ color:'var(--amber)' }}>Yellow</span> checklist pending</span>
        <span>● <span style={{ color:'var(--red)' }}>Red</span> open incident</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
        {grid.map(c => (
          <button key={c.campus_id} onClick={() => onPick(c.campus_id)}
            style={{ textAlign:'left', cursor:'pointer', borderRadius:'var(--radius)', padding:'12px',
              background:'var(--bg2)', border:`1px solid var(--border)`, borderLeft:`4px solid ${COLOR[c.color]}` }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>{c.campus_name}</div>
            <div style={{ fontSize:11, color:'var(--text2)' }}>
              {c.checklist_status === 'complete' ? '✓ Submitted' : c.checklist_status === 'missed' ? 'Missed' : 'Not submitted'}
            </div>
            {parseInt(c.open_incidents) > 0 && <div style={{ fontSize:11, color:'var(--red)', marginTop:2 }}>{c.open_incidents} open incident(s)</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── History ─────────────────────────────────────────────────────────────────
function History({ campuses }) {
  const [date, setDate] = useState('');
  const [campus, setCampus] = useState('');
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    const params = {};
    if (date) params.date = date;
    if (campus) params.campus_id = campus;
    api.get('/health-checks/history', { params }).then(r => setRows(r.data)).catch(()=>{});
  }, [date, campus]);
  useEffect(() => { load(); }, [load]);
  const badge = s => <span className={`badge ${s==='complete'?'badge-completed':s==='missed'?'badge-faulty':'badge-pending'}`}>{s}</span>;
  return (
    <div>
      <div className="flex-gap" style={{ gap:10, marginBottom:14 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding:'6px 8px' }} />
        <select value={campus} onChange={e => setCampus(e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All my campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {(date||campus) && <button className="btn btn-secondary btn-sm" onClick={() => { setDate(''); setCampus(''); }}>Clear</button>}
      </div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No checklists found.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Campus</th><th>Status</th><th>Submitted By</th><th>Issues</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize:13 }}>{new Date(r.check_date).toLocaleDateString()}</td>
                  <td style={{ fontSize:13 }}>{r.campus_name}</td>
                  <td>{badge(r.status)}</td>
                  <td style={{ fontSize:13, color:'var(--text2)' }}>{r.submitted_by_name || '—'}</td>
                  <td style={{ fontSize:13 }}>{parseInt(r.issues)>0 ? <span style={{ color:'var(--red)' }}>{r.issues}</span> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ─── Device registry (admin) ─────────────────────────────────────────────────
function DeviceRegistry({ campuses }) {
  const [campus, setCampus] = useState(campuses[0] ? String(campuses[0].id) : '');
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);   // {id?, device_name, parameters(str), sort_order}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!campus) return;
    api.get(`/health-checks/devices/${campus}`).then(r => setRows(r.data)).catch(()=>{});
  }, [campus]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setModal({ device_name:'', parameters:'', sort_order: rows.length+1 });
  const openEdit = d => setModal({ id:d.id, device_name:d.device_name, parameters:(d.parameters||[]).join(', '), sort_order:d.sort_order });
  const save = async () => {
    const params = modal.parameters.split(',').map(s => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      const body = { campus_id:parseInt(campus), device_name:modal.device_name.trim(), parameters:params, sort_order:parseInt(modal.sort_order)||0 };
      if (modal.id) await api.put(`/health-checks/devices/${modal.id}`, body);
      else await api.post('/health-checks/devices', body);
      setModal(null); load();
    } finally { setBusy(false); }
  };
  const del = async d => { if (!window.confirm(`Delete ${d.device_name}?`)) return; await api.delete(`/health-checks/devices/${d.id}`); load(); };

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:14 }}>
        <select value={campus} onChange={e => setCampus(e.target.value)} style={{ padding:'6px 8px', minWidth:200 }}>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Device</button>
      </div>
      <p style={{ fontSize:12, color:'var(--text2)', marginBottom:10 }}>These devices generate each morning's checklist for this campus. ISP slots vary per campus — edit the Firewall parameters to match (e.g. “ISP 1 - ACT”, “ISP 2 - BSNL”).</p>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No devices configured.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Device</th><th>Parameters</th><th>Order</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.id}>
                  <td style={{ fontSize:13, fontWeight:500 }}>{d.device_name}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{(d.parameters||[]).join(' · ')}</td>
                  <td style={{ fontSize:12 }}>{d.sort_order}</td>
                  <td style={{ fontSize:12 }}>{d.is_active ? '✓' : '—'}</td>
                  <td><div className="flex-gap" style={{ gap:6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(d)}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => del(d)}>Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:480 }}>
            <div className="modal-header"><span className="modal-title">{modal.id?'Edit':'Add'} Device</span><button className="modal-close" onClick={() => setModal(null)}>×</button></div>
            <div className="form-group"><label>Device Name *</label><input value={modal.device_name} onChange={e => setModal(m => ({ ...m, device_name:e.target.value }))} placeholder="e.g. Firewall" /></div>
            <div className="form-group"><label>Parameters (comma-separated) *</label>
              <textarea rows={3} value={modal.parameters} onChange={e => setModal(m => ({ ...m, parameters:e.target.value }))} placeholder="Power, CPU Usage, RAM, ISP 1 - ACT, ISP 2 - BSNL" /></div>
            <div className="form-group" style={{ maxWidth:120 }}><label>Order</label><input type="number" value={modal.sort_order} onChange={e => setModal(m => ({ ...m, sort_order:e.target.value }))} /></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy || !modal.device_name.trim()}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────
export default function HealthChecks() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState('today');
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');

  useEffect(() => {
    api.get('/master/campuses').then(r => {
      const list = r.data.filter(c => c.active && c.health_check_required !== false && (isAdmin || (user.campus_ids||[]).includes(c.id)));
      setCampuses(list);
      setCampusId(String((user.campus_ids||[])[0] || list[0]?.id || ''));
    }).catch(()=>{});
  }, [isAdmin, user]);

  const tabs = [['today','Today\'s Check'],['board','Status Board'],['history','History']];
  if (isAdmin) tabs.push(['devices','Device Registry']);

  return (
    <div>
      <div style={{ marginBottom:18 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Daily Health Checks</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Morning infrastructure check, incidents and escalation across campuses.</p>
      </div>

      <div style={{ display:'flex', gap:0, marginBottom:18, borderBottom:'1px solid var(--border)' }}>
        {tabs.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'9px 18px', fontSize:13, cursor:'pointer', background:'transparent', border:'none',
              borderBottom: tab===id?'2px solid var(--accent)':'2px solid transparent',
              color: tab===id?'var(--accent)':'var(--text2)', fontWeight: tab===id?600:400, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>

      {(tab === 'today') && (
        <>
          {campuses.length > 1 && (
            <div className="form-group" style={{ maxWidth:240, marginBottom:14 }}>
              <label>Campus</label>
              <select value={campusId} onChange={e => setCampusId(e.target.value)}>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <TodayCheck campusId={campusId} />
        </>
      )}
      {tab === 'board'   && <StatusBoard onPick={id => { setCampusId(String(id)); setTab('today'); }} />}
      {tab === 'history' && <History campuses={campuses} />}
      {tab === 'devices' && isAdmin && <DeviceRegistry campuses={campuses} />}
    </div>
  );
}
